/* CasualWages.jsx — End-of-day casual labour wage page
 *
 * Workflow:
 *   1. Pick the casual
 *   2. Enter hours+rate OR lump sum
 *   3. Click Pay Now → cash leaves the till as PETTY CASH, an attendance
 *      row is recorded, and an 80mm wage slip prints for signing.
 *   4. At month-end the payroll preview adds these up automatically.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Banknote, Printer, Trash2, Loader2, RefreshCw, Plus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => "R " + Math.round(Number(n || 0)).toLocaleString("en-ZA");

const todaySAST = () => {
  const d = new Date();
  return new Date(d.getTime() + (2 * 60 - d.getTimezoneOffset()) * 60000).toISOString().slice(0, 10);
};

export default function CasualWages() {
  const [employees, setEmployees] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [wages, setWages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState(todaySAST().slice(0, 7)); // YYYY-MM
  const [paying, setPaying] = useState(false);

  // Form
  const [empId, setEmpId] = useState("");
  const [date, setDate] = useState(todaySAST());
  const [mode, setMode] = useState("hourly");  // "hourly" | "lump"
  const [hours, setHours] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [lumpSum, setLumpSum] = useState("");
  const [department, setDepartment] = useState("");
  const [note, setNote] = useState("");

  const computedAmount = useMemo(() => {
    if (mode === "lump") return Math.round(Number(lumpSum) || 0);
    return Math.round((Number(hours) || 0) * (Number(hourlyRate) || 0));
  }, [mode, hours, hourlyRate, lumpSum]);

  const load = async () => {
    setLoading(true);
    try {
      const [emp, w, settings] = await Promise.all([
        axios.get(`${API}/api/employees`),
        axios.get(`${API}/api/payroll/casual/list`, {
          params: {
            start_date: `${filterMonth}-01`,
            end_date: `${filterMonth}-31`,
          },
        }),
        axios.get(`${API}/api/settings`).catch(() => ({ data: {} })),
      ]);
      const casuals = (emp.data || []).filter(e => e.is_active && String(e.salary_type || "").toLowerCase() === "casual");
      setEmployees(casuals);
      setWages(w.data.rows || []);
      setCompanyName(settings.data?.company_name || "");
      if (casuals.length && !empId) setEmpId(casuals[0].id);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterMonth]);

  const selectedEmp = employees.find(e => e.id === empId);

  const pay = async () => {
    if (!empId) { toast.error("Pick an employee"); return; }
    if (computedAmount <= 0) { toast.error("Enter amount/hours first"); return; }
    setPaying(true);
    try {
      const body = {
        employee_id: empId,
        date,
        department,
        note,
      };
      if (mode === "lump") body.lump_sum = Number(lumpSum);
      else { body.hours = Number(hours); body.hourly_rate = Number(hourlyRate); }
      const res = await saveWithRetry(() => axios.post(`${API}/api/payroll/casual/pay`, body));
      toast.success(`Paid ${fmt(res.data.amount)} to ${res.data.employee_name}. Slip ${res.data.wage_slip_number} ready to print.`);
      printSlip(res.data);
      // Reset
      setHours(""); setLumpSum(""); setNote("");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Pay failed"));
    } finally {
      setPaying(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this wage record? The till cash-out will be reversed too.")) return;
    try {
      await saveWithRetry(() => axios.delete(`${API}/api/payroll/casual/${id}`));
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Delete failed"));
    }
  };

  const totalThisMonth = useMemo(
    () => wages.reduce((s, w) => s + Number(w.amount || 0), 0),
    [wages]
  );

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="casual-wages-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Banknote className="h-6 w-6 text-emerald-600" /> Casual Wages — Daily Pay
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          End-of-day cash payment to casual workers. Auto-deducts from the till (Petty Cash),
          creates an attendance record, and prints a slip for the worker to sign.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* PAY FORM */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4 text-emerald-600" />
              Pay a casual
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {employees.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                No staff with <code>salary_type = casual</code>. Go to Employees → edit a worker and set salary type to <strong>"casual"</strong>.
              </div>
            ) : (
              <>
                <div>
                  <Label>Employee</Label>
                  <Select value={empId} onValueChange={setEmpId}>
                    <SelectTrigger data-testid="cw-employee"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.first_name} {e.last_name} — {e.position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={date} max={todaySAST()} onChange={e => setDate(e.target.value)} data-testid="cw-date" />
                  </div>
                  <div>
                    <Label>Department (optional)</Label>
                    <Input value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g. Warehouse" />
                  </div>
                </div>

                {/* Mode switch */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={mode === "hourly" ? "default" : "outline"}
                    onClick={() => setMode("hourly")}
                    className={mode === "hourly" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    data-testid="cw-mode-hourly"
                  >
                    Hours × Rate
                  </Button>
                  <Button
                    size="sm"
                    variant={mode === "lump" ? "default" : "outline"}
                    onClick={() => setMode("lump")}
                    className={mode === "lump" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    data-testid="cw-mode-lump"
                  >
                    Lump sum
                  </Button>
                </div>

                {mode === "hourly" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Hours worked</Label>
                      <Input type="number" step="0.25" inputMode="decimal" value={hours} onChange={e => setHours(e.target.value)} placeholder="8" data-testid="cw-hours" />
                    </div>
                    <div>
                      <Label>Hourly rate (R)</Label>
                      <Input type="number" step="0.5" inputMode="decimal" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder={selectedEmp?.basic_salary || "25"} data-testid="cw-rate" />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>Lump sum to pay (R)</Label>
                    <Input type="number" step="1" inputMode="decimal" value={lumpSum} onChange={e => setLumpSum(e.target.value)} placeholder="e.g. 200" data-testid="cw-lump" />
                  </div>
                )}

                <div>
                  <Label>Note (optional)</Label>
                  <Input value={note} onChange={e => setNote(e.target.value)} placeholder="What did they work on?" />
                </div>

                <div className="bg-emerald-50 border-2 border-emerald-300 rounded p-3 flex items-center justify-between">
                  <div className="text-emerald-900 text-sm">Amount to pay (from till)</div>
                  <div className="text-3xl font-bold text-emerald-700" data-testid="cw-amount">{fmt(computedAmount)}</div>
                </div>

                <Button
                  onClick={pay}
                  disabled={paying || computedAmount <= 0}
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base"
                  data-testid="cw-pay-btn"
                >
                  {paying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Banknote className="h-4 w-4 mr-1" />}
                  Pay & print slip
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* MONTH TOTALS PER PERSON */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">{filterMonth} totals</CardTitle>
              <p className="text-xs text-gray-500">Per casual, what's been paid this month</p>
            </div>
            <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="w-40" />
          </CardHeader>
          <CardContent>
            {(() => {
              const byEmp = {};
              wages.forEach(w => {
                if (!byEmp[w.employee_id]) byEmp[w.employee_id] = { name: w.employee_name, total: 0, days: 0 };
                byEmp[w.employee_id].total += Number(w.amount || 0);
                byEmp[w.employee_id].days += 1;
              });
              const rows = Object.values(byEmp).sort((a, b) => b.total - a.total);
              if (rows.length === 0) return <p className="text-sm text-gray-400 italic py-4 text-center">No payments this month.</p>;
              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Casual</TableHead>
                      <TableHead className="text-right">Days paid</TableHead>
                      <TableHead className="text-right">Total paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-right">{r.days}</TableCell>
                        <TableCell className="text-right font-bold text-emerald-700">{fmt(r.total)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-emerald-50">
                      <TableCell className="font-bold">TOTAL</TableCell>
                      <TableCell className="text-right font-bold">{wages.length}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-700 text-lg">{fmt(totalThisMonth)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* RECENT PAYMENTS */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Recent payments</CardTitle>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </CardHeader>
        <CardContent>
          {wages.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-4 text-center">No casual wages in {filterMonth} yet.</p>
          ) : (
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Slip #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Basis</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wages.map(w => (
                  <TableRow key={w.id}>
                    <TableCell className="font-mono text-xs">{w.wage_slip_number}</TableCell>
                    <TableCell>{w.date}</TableCell>
                    <TableCell className="font-medium">{w.employee_name}</TableCell>
                    <TableCell>
                      {w.wage_basis === "hourly"
                        ? <Badge variant="outline" className="text-[10px]">{w.hours}h × R{w.hourly_rate}</Badge>
                        : <Badge variant="outline" className="text-[10px]">Lump R{w.lump_sum}</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-gray-600">{w.department || "—"}</TableCell>
                    <TableCell className="text-xs text-gray-600 max-w-[180px] truncate" title={w.note}>{w.note || "—"}</TableCell>
                    <TableCell className="text-right font-bold text-emerald-700">{fmt(w.amount)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => printSlip(w, companyName)} title="Reprint slip">
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(w.id)} className="text-red-600" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------- printable 80mm wage slip ---------- */
function printSlip(w, companyName = "") {
  const win = window.open("", "_blank", "width=400,height=600");
  if (!win) { toast.error("Pop-up blocked"); return; }
  const safe = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const html = `
<!DOCTYPE html><html><head><title>Wage Slip ${safe(w.wage_slip_number)}</title>
<style>
@page { size: 80mm auto; margin: 4mm; }
body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; }
h1 { font-size: 14px; margin: 0 0 4px 0; text-align: center; }
hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
.line { display: flex; justify-content: space-between; }
.lbl { font-weight: bold; }
.big { font-size: 18px; font-weight: bold; text-align: center; padding: 4px; border: 2px solid #000; margin: 6px 0; }
.sig { margin-top: 12px; }
.sig-line { border-bottom: 1px solid #000; height: 16px; }
.sub { font-size: 9px; color: #555; text-align: center; }
</style></head><body>
<h1>${safe(companyName || "WAGE SLIP")}</h1>
<div class="sub">CASUAL WAGE — DAILY PAYMENT</div>
<hr/>
<div class="line"><span class="lbl">Slip #</span><span>${safe(w.wage_slip_number)}</span></div>
<div class="line"><span class="lbl">Date</span><span>${safe(w.date)}</span></div>
<div class="line"><span class="lbl">Petty cash</span><span>${safe(w.petty_cash_number || "")}</span></div>
<hr/>
<div class="line"><span class="lbl">Worker</span><span>${safe(w.employee_name)}</span></div>
${w.department ? `<div class="line"><span class="lbl">Dept</span><span>${safe(w.department)}</span></div>` : ""}
<hr/>
${w.wage_basis === "hourly" ? `
  <div class="line"><span class="lbl">Hours</span><span>${Number(w.hours).toFixed(2)}</span></div>
  <div class="line"><span class="lbl">Rate /hr</span><span>R ${Number(w.hourly_rate).toFixed(2)}</span></div>
` : `
  <div class="line"><span class="lbl">Lump sum</span><span>R ${Number(w.lump_sum).toFixed(2)}</span></div>
`}
${w.note ? `<div class="line" style="margin-top:4px"><span class="lbl">Note</span><span>${safe(w.note)}</span></div>` : ""}
<div class="big">PAID  R ${Math.round(Number(w.amount)).toLocaleString("en-ZA")}</div>
<div class="sig">
  <div class="sub">Worker signature (acknowledges receipt of cash)</div>
  <div class="sig-line"></div>
</div>
<div class="sig">
  <div class="sub">Paid by</div>
  <div class="sig-line">${safe(w.paid_by_user_name || "")}</div>
</div>
<hr/>
<div class="sub">Printed ${new Date().toLocaleString("en-ZA")}</div>
<script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };</script>
</body></html>`;
  win.document.write(html);
  win.document.close();
}
